import React, { useCallback, useState } from 'react'

export type TError<T> = {
  detail?: string
} & {
  [key in keyof T]?: React.ReactElement | string
}

type TStatus<T> = {
  [key in keyof T]?: 'error' | 'valid'
}

export type FormState<T> = {
  data: T
  errors: TError<T>
  statuses: TStatus<T>
}

type Props<T> = {
  defaultData: T
  validateForm: (
    formData: Partial<T>,
    keys: (keyof T)[],
  ) => { [key in keyof T]?: string | React.ReactElement }
}

export const useFormDataV2 = <T>({ defaultData, validateForm }: Props<T>) => {
  const [form, setForm] = useState<FormState<T>>({
    data: defaultData,
    errors: {},
    statuses: {},
  })

  const onInputChange = useCallback(
    (
      field: keyof T,
      validateCustom?: (data: T) => { [key in keyof T]?: string | React.ReactElement },
      transform: (
        event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
        data: FormState<T>,
      ) => T[typeof field] = (event) => {
        const { value } = event.target

        return value.trimStart() as T[typeof field]
      },
    ) =>
      (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setForm((data) => {
          const newData = {
            ...data.data,
            [field]: transform(event, data),
          }
          let errors = validateForm(newData, [field])
          if (validateCustom) errors = { ...errors, ...validateCustom(newData) }
          return {
            data: newData,
            errors: { ...data.errors, ...errors, detail: undefined },
            statuses: {
              ...data.statuses,
              ...Object.entries(errors).reduce(
                (acc, [key, error]) => ({ ...acc, [key]: error ? 'error' : 'valid' }),
                {},
              ),
            },
          }
        })
      },
    [validateForm],
  )

  const onValidate = useCallback(
    (...keys: (keyof T)[]) =>
      (callback?: ((data: T) => void) | React.SyntheticEvent) => {
        setForm((data) => {
          const trimmedData = keys.reduce<T>((acc, key) => {
            if (typeof acc[key] !== 'string') return acc
            return { ...acc, [key]: acc[key]?.toString().trim() }
          }, data.data)

          const errors = validateForm(trimmedData, keys)

          if (!Object.values(errors).some(Boolean) && typeof callback === 'function') {
            callback(trimmedData)
          }

          return {
            data: trimmedData,
            statuses: {
              ...data.statuses,
              ...Object.entries(errors).reduce(
                (acc, [key, error]) => ({ ...acc, [key]: error ? 'error' : 'valid' }),
                {},
              ),
            },
            errors: { ...data.errors, ...errors },
          }
        })
      },
    [validateForm],
  )

  const onReset = useCallback(() => {
    setForm({
      data: defaultData,
      errors: {},
      statuses: {},
    })
  }, [defaultData])

  return {
    form,
    setForm,
    onReset,
    onValidate,
    onInputChange,
  }
}
